import {
  amber,
  amberDark,
  blue,
  blueDark,
  cyan,
  cyanDark,
  indigo,
  grass,
  grassDark,
  mauve,
  mauveDark,
  mint,
  olive,
  oliveDark,
  plumDark,
  sand,
  sandDark,
  sky,
  slate,
  teal,
  violet,
  violetDark,
  slateDark,
  indigoDark,
} from "@radix-ui/colors";
import {
  ThemePalette,
  ThemePreferences,
  DEFAULT_THEME_PREFERENCES,
} from "@/types/preferences";

export interface ThemePreset {
  id: string;
  name: string;
  mode: "light" | "dark";
  description: string;
  palette: ThemePalette;
  swatch: string[];
}

export const DEFAULT_LIGHT_THEME_ID = "classic";
export const DEFAULT_DARK_THEME_ID = "midnight";

const swatchFromPalette = (palette: ThemePalette) => [
  palette.background,
  palette.surface,
  palette.primary,
  palette.accent,
  palette.foreground,
];

const preset = (
  id: string,
  name: string,
  mode: "light" | "dark",
  description: string,
  palette: ThemePalette
): ThemePreset => ({
  id,
  name,
  mode,
  description,
  palette,
  swatch: swatchFromPalette(palette),
});

export const LIGHT_THEME_PRESETS: ThemePreset[] = [
  preset(
    "classic",
    "Classic",
    "light",
    "Softer slate surfaces with a bright blue accent.",
    {
      background: mauve.mauve1,
      surface: mauve.mauve3,
      foreground: mauve.mauve12,
      primary: indigo.indigo9,
      accent: blueDark.blue11,
    }
  ),
  preset(
    "sunrise",
    "Sunrise",
    "light",
    "Warm amber base with soft sand neutrals.",
    {
      background: sand.sand1,
      surface: sand.sand3,
      foreground: sand.sand12,
      primary: amber.amber11,
      accent: amber.amber9,
    }
  ),
  preset(
    "evergreen",
    "Evergreen",
    "light",
    "Leafy greens with fresh teal highlights.",
    {
      background: mint.mint1,
      surface: mint.mint2,
      foreground: olive.olive12,
      primary: grass.grass10,
      accent: teal.teal10,
    }
  ),
  preset(
    "violet-bloom",
    "Violet Bloom",
    "light",
    "Violet gradients with calm mauve surfaces.",
    {
      background: violet.violet1,
      surface: violet.violet3,
      foreground: violet.violet12,
      primary: violet.violet10,
      accent: mauve.mauve11,
    }
  ),
  preset(
    "seaside",
    "Seaside",
    "light",
    "Sky tones inspired by North Sea mornings.",
    {
      background: sky.sky1,
      surface: sky.sky3,
      foreground: sky.sky12,
      primary: cyan.cyan10,
      accent: teal.teal9,
    }
  ),
];

export const DARK_THEME_PRESETS: ThemePreset[] = [
  preset(
    "midnight",
    "Midnight",
    "dark",
    "Slate midnight with electric blue focus.",
    {
      background: blueDark.blue2,
      surface: mauveDark.mauve1,
      foreground: mauveDark.mauve12,
      primary: indigo.indigo9,
      accent: blue.blue5,
    }
  ),
  preset("ember", "Ember", "dark", "Golden embers on deep charcoal.", {
    background: mauveDark.mauve1,
    surface: mauveDark.mauve3,
    foreground: mauveDark.mauve12,
    primary: amberDark.amber11,
    accent: amberDark.amber9,
  }),
  preset(
    "forest-night",
    "Forest Night",
    "dark",
    "Earthy green focus with dim woodland surfaces.",
    {
      background: oliveDark.olive1,
      surface: oliveDark.olive3,
      foreground: oliveDark.olive12,
      primary: grassDark.grass10,
      accent: teal.teal10,
    }
  ),
  preset(
    "afterglow",
    "Afterglow",
    "dark",
    "Plum twilight with warm violet accents.",
    {
      background: mauveDark.mauve1,
      surface: mauveDark.mauve3,
      foreground: mauveDark.mauve12,
      primary: plumDark.plum11,
      accent: violetDark.violet10,
    }
  ),
  preset("neon", "Neon", "dark", "Cyan glow on a deep charcoal base.", {
    background: mauveDark.mauve2,
    surface: mauveDark.mauve3,
    foreground: mauveDark.mauve12,
    primary: cyanDark.cyan11,
    accent: cyanDark.cyan9,
  }),
];

export const RADIX_SWATCHES: Record<string, string[]> = {
  blue: [blue.blue1, blue.blue3, blue.blue12, blue.blue11, blue.blue9],
  teal: [teal.teal1, teal.teal3, teal.teal12, teal.teal11, teal.teal9],
  violet: [
    violet.violet1,
    violet.violet3,
    violet.violet12,
    violet.violet11,
    violet.violet9,
  ],
  amber: [
    amber.amber1,
    amber.amber3,
    amber.amber12,
    amber.amber11,
    amber.amber9,
  ],
  grass: [
    grass.grass1,
    grass.grass3,
    grass.grass12,
    grass.grass11,
    grass.grass9,
  ],
  cyan: [cyan.cyan1, cyan.cyan3, cyan.cyan12, cyan.cyan11, cyan.cyan9],
  mint: [mint.mint1, mint.mint3, mint.mint12, mint.mint11, mint.mint9],
  sand: [sand.sand1, sand.sand3, sand.sand12, sand.sand11, sand.sand9],
};

export const ALL_THEME_PRESETS = [
  ...LIGHT_THEME_PRESETS,
  ...DARK_THEME_PRESETS,
];

const LEGACY_THEME_ALIASES: Record<string, string> = {
  aqua: "evergreen",
  aurora: "ember",
};

const normalizePresetId = (id: string) => LEGACY_THEME_ALIASES[id] ?? id;

export const getPresetById = (id: string) =>
  ALL_THEME_PRESETS.find((preset) => preset.id === normalizePresetId(id));

export const resolvePaletteForTheme = (
  id: string,
  tone: "light" | "dark",
  prefs?: ThemePreferences
): ThemePalette => {
  if (id === "custom") {
    const fallback =
      tone === "dark"
        ? DEFAULT_THEME_PREFERENCES.customTheme.dark
        : DEFAULT_THEME_PREFERENCES.customTheme.light;
    const custom = prefs?.customTheme;
    return custom
      ? tone === "dark"
        ? { ...fallback, ...custom.dark }
        : { ...fallback, ...custom.light }
      : fallback;
  }
  const preset = getPresetById(id);
  if (preset && preset.mode === tone) {
    return preset.palette;
  }
  const fallbackPreset =
    tone === "dark"
      ? getPresetById(DEFAULT_DARK_THEME_ID)
      : getPresetById(DEFAULT_LIGHT_THEME_ID);
  return (
    fallbackPreset?.palette ||
    (tone === "dark"
      ? DEFAULT_THEME_PREFERENCES.customTheme.dark
      : DEFAULT_THEME_PREFERENCES.customTheme.light)
  );
};
